/**
 * `surfacekit/form.js` wired into React (`liquid-page-runtime.md` §5).
 *
 * §5 says the shell's own settings and request forms are the same behaviour
 * as a plugin page's forms, and that the two must be one state machine rather
 * than two implementations. The machine is `surfacekit/form.js`; a plugin page
 * runs it inside the sandbox through `bridge.js`, and this is what it runs in
 * here. Nothing about the rules is repeated below -- what a draft is, when a
 * save confirms, what the typing does when the content moves, and what must
 * never be called 已保存 all live in that one file.
 *
 * What is here is the three things the machine asks a runtime for, answered the
 * way a React tree can answer them:
 *
 * - the projected state is whatever this render was handed, read through a ref
 *   so the machine always sees the current props rather than the ones it was
 *   built with;
 * - "it moved" is a render with different reads, which is why the effect below
 *   is keyed on the reads and not on a clock;
 * - a draft goes to `sessionStorage`, which is tab-local and is never evidence
 *   that anything was saved -- a write that fails rejects, and the machine turns
 *   that into `persisted: false` with the reason.
 *
 * A submission is followed by its obligation, through the same `command` frames
 * every other part of this console listens to (§7.194). The machine only ever
 * asks two things of it: run this, and tell me when it settles.
 */

import { useEffect, useRef, useState } from "react";
import { createFormMachine } from "../../../surfacekit/form.js";
import type { FormAction, FormDraft, FormMachine, FormOptions, FormRead, FormView } from "../../../surfacekit/form";
import { clearDraft, readDraft, writeDraft } from "../lib/drafts";
import { watchCommand } from "../lib/commandFeed";

/** How a runtime keeps this form's draft, and what must never go into one. */
export interface DraftStore {
  /** False for a form nobody may keep a copy of; the machine still runs. */
  persist?: boolean;
  /**
   * The draft, with anything write-only taken out of it.
   *
   * A secret is write-only in both directions, so it is not content, not a
   * draft and not a version; the caller says here what that means for its own
   * fields, because only the declaration knows which ones they are.
   */
  redact?: (value: unknown) => unknown;
}

export type { FormRead, FormView };

/**
 * One form, driven by the shared machine.
 *
 * `reads` is this form's projected state, in the machine's own shape: the
 * caller has already found which declared read carries the content and where
 * its version is, because that is a page's business and not a machine's.
 */
export function useFormMachine(
  options: FormOptions,
  reads: FormRead[],
  store: DraftStore = {},
): readonly [FormView, FormMachine] {
  const current = useRef(reads);
  current.current = reads;
  const keep = useRef(store);
  keep.current = store;
  const drafts = useRef<Record<string, FormDraft | undefined>>({});
  const moved = useRef<(() => void) | null>(null);
  const follow = useRef<(() => void) | null>(null);
  const [machine] = useState<FormMachine>(() => {
    const name = String(options.draft || "");
    // What this tab was left holding. An entry written by an older shape of
    // this file has no `value`, which the machine reads as no draft at all --
    // the one thing it must not do is treat a shape it cannot read as content.
    drafts.current = name && store.persist !== false
      ? { [name]: readDraft<FormDraft | undefined>(name, undefined) }
      : {};
    return createFormMachine(options, {
      snapshot: () => ({ reads: current.current, drafts: drafts.current }),
      watch: (fn) => {
        follow.current = fn;
        moved.current = fn;
        return () => { moved.current = null; };
      },
      saveDraft: (key, entry) => {
        drafts.current = { ...drafts.current, [key]: entry ?? undefined };
        if (!key || keep.current.persist === false) return Promise.resolve();
        try {
          if (entry === null) clearDraft(key);
          else writeDraft(key, { ...entry, value: (keep.current.redact ?? ((v) => v))(entry.value) });
          return Promise.resolve();
        } catch (error) {
          return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }
      },
      // Somebody else's callback throwing is not this form's failure, and the
      // browser console is where a shell reports its own bugs.
      guard: (fn, value, where) => {
        try { fn(value as never); } catch (error) { console.error(where, error); }
      },
    });
  });
  const [view, setView] = useState<FormView>(() => machine.state());
  useEffect(() => machine.subscribe(setView), [machine]);
  // Watching while mounted. React may unmount and mount the same component
  // again, keeping its state (StrictMode does, once): the machine kept in that
  // state was stopped, so the second mount takes its watch back. Declared before
  // the effect below, which then tells it about the reads it missed.
  useEffect(() => {
    moved.current = follow.current;
    return () => machine.stop();
  }, [machine]);
  // The projected state moved. Keyed on the reads themselves: a render that
  // changed nothing the machine can see is not news, and telling it anyway
  // would be this file inventing a clock.
  const signature = JSON.stringify(reads);
  useEffect(() => { moved.current?.(); }, [signature]);
  return [view, machine] as const;
}

/**
 * One press whose write answers for itself (ruling 204).
 *
 * The owner's settings save is a direct write to the namespace, not a command
 * the core follows: what `run` resolves with is the outcome, and what it
 * rejects with is the reason.
 */
export function directSubmission(
  run: (params: Record<string, unknown>) => Promise<void>,
): FormAction {
  let tell: Parameters<FormAction["subscribe"]>[0] | null = null;
  return {
    // Settled the moment the write answers: there is no obligation behind a
    // direct write, so its answer is the whole outcome. A rejection is the
    // machine's ordinary reading of a `run` that threw -- the sentence on the
    // form, the draft kept.
    run: async (params) => {
      await run(params);
      tell?.({ state: "completed" });
    },
    subscribe: (fn) => {
      tell = fn;
      return () => { tell = null; };
    },
  };
}

/**
 * One press, as the machine reads a submission: run it, then say when it ended.
 *
 * `run` hands the values over and answers with the obligation the core gave
 * them. Until that obligation settles nothing here claims anything: the
 * machine's `saving` is the whole of what the page may say, and 已保存 waits
 * for a `completed` that the projection wrote.
 *
 * `""` back from `run` means the press produced nothing to follow -- it was
 * refused before it left, or the page has no seat yet, or there was nothing
 * changed to send. Whatever the reason, this page cannot say a save happened,
 * so it says the one thing it knows and keeps the draft exactly where it is.
 * Silence would be worse: the form would sit on 正在保存 forever.
 */
export function commandSubmission(
  run: (params: Record<string, unknown>) => Promise<string>,
): FormAction {
  let tell: Parameters<FormAction["subscribe"]>[0] | null = null;
  let release: (() => void) | null = null;
  let over = false;
  return {
    // The params are the machine's, not the live form's: what it captured at
    // the press is what gets sent, which is the whole of "a save confirms the
    // version it submitted and no other".
    run: async (params) => {
      const obligation = await run(params);
      if (!obligation) {
        tell?.({ state: "stopped", error: "这一步没有提交出去，填的内容留着。" });
        return;
      }
      const stop = watchCommand(obligation, (status) => {
        tell?.({ state: status.action_state || "", error: status.error, effects: status.effects });
      });
      // Settled while the submission was still on its way out: nobody is
      // listening any more, so nothing is left subscribed either.
      if (over) stop();
      else release = stop;
    },
    subscribe: (fn) => {
      tell = fn;
      return () => { over = true; release?.(); release = null; tell = null; };
    },
  };
}
